import React from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  Shield,
  Sun,
  Moon,
  Monitor,
  Activity,
  LogOut,
  KeyRound,
  ChevronDown,
  Lock,
  Check,
  Network,
  Router,
  Wrench,
  Code2,
  } from 'lucide-react';
import { useAuth } from '../../contexts/auth.js';
import { type OrgMembership, useOrgContext } from '../../contexts/org.js';
import { useTheme, type ThemePreference } from '../../contexts/theme.js';

interface SidebarProps {
  mobile?: boolean;
  onNavigate?: () => void;
}

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  end?: boolean;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

type CockpitId = 'platform' | 'workspace';

function buildPlatformNav(): NavSection[] {
  return [
    {
      items: [
        { label: 'Overview', href: '/operator', icon: <LayoutDashboard className="h-4 w-4" />, end: true },
      ],
    },
    {
      title: 'GATEWAY',
      items: [
        { label: 'Model Gateway', href: '/operator/settings/model-gateway', icon: <Network className="h-4 w-4" /> },
        { label: 'Pre-flight Checks', href: '/operator/settings/preflight-checks', icon: <Shield className="h-4 w-4" /> },
        { label: 'Developer Guide', href: '/operator/developer-guide', icon: <Code2 className="h-4 w-4" /> },
      ],
    },
    {
      title: 'ACCESS',
      items: [
        { label: 'API Keys', href: '/operator/api-keys', icon: <KeyRound className="h-4 w-4" /> },
      ],
    },
    {
      title: 'OPERATIONS',
      items: [
        { label: 'Monitoring', href: '/operator/monitoring', icon: <Activity className="h-4 w-4" /> },
      ],
    },
  ];
}

function detectCockpit(pathname: string): CockpitId {
  if (pathname.startsWith('/admin') || pathname.startsWith('/operator')) return 'platform';
  if (pathname.startsWith('/settings/')) return 'platform';
  return 'platform'; // default to platform nav for the router
}

function formatRoleLabel(role?: string): string {
  if (!role) {
    return 'Member';
  }

  return role.charAt(0).toUpperCase() + role.slice(1);
}

function OrgSelector({
  title,
  currentOrg,
  options,
  open,
  onToggle,
  onSelect,
}: {
  title: string;
  currentOrg: Pick<OrgMembership, 'orgId' | 'orgName' | 'role'> | null;
  options: Pick<OrgMembership, 'orgId' | 'orgName' | 'role'>[];
  open: boolean;
  onToggle: () => void;
  onSelect: (orgId: string) => void;
}) {
  if (!currentOrg) {
    return null;
  }

  const multipleOptions = options.length > 1;

  return (
    <div className="border-b border-white/10 px-3 py-3">
      <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/30">
        {title}
      </p>
      <div className="relative">
        {multipleOptions ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center gap-2.5 rounded-lg bg-white/5 px-3 py-2.5 text-left transition-colors hover:bg-white/8"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-500/15 text-xs font-bold text-emerald-300">
              {currentOrg.orgName[0]?.toUpperCase() ?? '?'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-semibold text-white/80">{currentOrg.orgName}</p>
              <p className="truncate text-[10px] text-white/30">{formatRoleLabel(currentOrg.role)}</p>
            </div>
            <ChevronDown className={`h-3.5 w-3.5 text-white/30 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        ) : (
          <div className="flex items-center gap-2.5 rounded-lg bg-white/5 px-3 py-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-500/15 text-xs font-bold text-emerald-300">
              {currentOrg.orgName[0]?.toUpperCase() ?? '?'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-semibold text-white/80">{currentOrg.orgName}</p>
              <p className="truncate text-[10px] text-white/30">{formatRoleLabel(currentOrg.role)}</p>
            </div>
          </div>
        )}

        {multipleOptions && open && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-white/10 bg-[rgba(20,20,40,0.95)] shadow-xl backdrop-blur-xl">
            {options.map((organization) => (
              <button
                key={organization.orgId}
                type="button"
                onClick={() => onSelect(organization.orgId)}
                className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                  organization.orgId === currentOrg.orgId
                    ? 'bg-emerald-500/10 text-emerald-200'
                    : 'text-white/55 hover:bg-white/5 hover:text-white/75'
                }`}
              >
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-emerald-500/15 text-[11px] font-bold text-emerald-300">
                  {organization.orgName[0]?.toUpperCase() ?? '?'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-medium">{organization.orgName}</p>
                  <p className="truncate text-[10px] text-white/30">{formatRoleLabel(organization.role)}</p>
                </div>
                {organization.orgId === currentOrg.orgId && <Check className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CloudSidebar({ mobile: _mobile, onNavigate }: SidebarProps) {
  const { claims, logout } = useAuth();
  const { memberships, selectedOrg, selectedOrgId, setSelectedOrgId } = useOrgContext();
  const { preference, setPreference } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ orgId?: string }>();
  const [orgMenuOpen, setOrgMenuOpen] = React.useState(false);

  const userInitial = (claims?.name?.[0] ?? claims?.email?.[0] ?? 'R').toUpperCase();
  const userPermissions = claims?.permissions ?? [];
  const isPlatformAdmin = userPermissions.includes('platform.admin');

  React.useEffect(() => {
    setOrgMenuOpen(false);
  }, [location.pathname, selectedOrgId]);

  const activeCockpit = detectCockpit(location.pathname);
  const routeOrgId = params.orgId && params.orgId !== 'me' ? params.orgId : null;
  const activeOrgId = routeOrgId ?? selectedOrgId ?? claims?.org_id ?? null;

  const sections: NavSection[] = buildPlatformNav();

  const showOrgContext = activeCockpit !== 'platform';

  const orgOptions = memberships;

  const currentOrg = orgOptions.find((organization) => organization.orgId === activeOrgId)
    ?? selectedOrg
    ?? null;

  function handleOrgSelection(nextOrgId: string) {
    setSelectedOrgId(nextOrgId);
    setOrgMenuOpen(false);

    // When on an /org/:orgId route, navigate to the same sub-page for the new org
    const orgRouteMatch = location.pathname.match(/^\/org\/([^/]+)(\/.*)?$/);
    if (orgRouteMatch) {
      const subPath = orgRouteMatch[2] ?? '';
      navigate(`/org/${nextOrgId}${subPath}`);
    }

    onNavigate?.();
  }

  function handlePlatformToggle() {
    if (activeCockpit === 'platform') {
      navigate('/workspace');
    } else {
      navigate('/operator');
    }
    onNavigate?.();
  }

  const isPlatformMode = activeCockpit === 'platform';

  return (
    <nav
      className={`flex h-dvh w-60 shrink-0 flex-col border-r backdrop-blur-xl transition-colors ${
        isPlatformMode
          ? 'border-amber-500/20 bg-[rgba(36,20,4,0.95)]'
          : 'border-white/10 bg-[rgba(26,26,46,0.80)]'
      }`}
    >
      <div className={`border-b px-5 py-5 ${
        isPlatformMode ? 'border-amber-500/20' : 'border-white/10'
      }`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Router className={`h-5 w-5 ${isPlatformMode ? 'text-amber-400' : 'text-indigo-400'}`} />
            <span className={`text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r ${
              isPlatformMode
                ? 'from-amber-400 via-orange-400 to-yellow-400'
                : 'from-indigo-400 via-purple-400 to-cyan-400'
            }`}>
              Rule42 Router
            </span>
          </div>
          {isPlatformAdmin && (
            <button
              type="button"
              onClick={handlePlatformToggle}
              title={isPlatformMode ? 'Exit Platform Admin' : 'Platform Admin'}
              className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                isPlatformMode
                  ? 'bg-amber-500/30 text-amber-200 hover:bg-amber-500/40 ring-1 ring-amber-500/40'
                  : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/60'
              }`}
            >
              <Wrench className="h-3 w-3" />
              Platform
            </button>
          )}
        </div>
        <p className={`mt-1 text-[10px] font-medium uppercase tracking-[0.2em] ${
          isPlatformMode ? 'text-amber-500/60' : 'text-white/30'
        }`}>
          {isPlatformMode ? 'Platform Admin Mode' : 'Control Plane'}
        </p>
      </div>

      {showOrgContext && (
        <OrgSelector
          title="Active Organization"
          currentOrg={currentOrg}
          options={orgOptions}
          open={orgMenuOpen}
          onToggle={() => setOrgMenuOpen((open) => !open)}
          onSelect={handleOrgSelection}
        />
      )}

      <div className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section, index) => (
          <div key={index} className={index > 0 ? 'mt-6' : ''}>
            {section.title && (
              <p className={`mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider ${
                isPlatformMode ? 'text-amber-500/50' : 'text-white/30'
              }`}>
                {section.title}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors ${
                      isActive
                        ? isPlatformMode ? 'bg-amber-500/15 font-semibold text-amber-300' : 'bg-indigo-500/15 font-semibold text-indigo-300'
                        : 'text-white/50 hover:bg-white/5 hover:text-white/70'
                    }`
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-6">
          <p className={`mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider ${
            isPlatformMode ? 'text-amber-500/50' : 'text-white/30'
          }`}>
            ACCOUNT
          </p>
          <div className="space-y-0.5">
            <NavLink
              to="/settings/account"
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors ${
                  isActive
                    ? isPlatformMode ? 'bg-amber-500/15 font-semibold text-amber-300' : 'bg-indigo-500/15 font-semibold text-indigo-300'
                    : 'text-white/50 hover:bg-white/5 hover:text-white/70'
                }`
              }
            >
              <Settings className="h-4 w-4" />
              My Account
            </NavLink>
            <NavLink
              to="/settings/security"
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors ${
                  isActive
                    ? isPlatformMode ? 'bg-amber-500/15 font-semibold text-amber-300' : 'bg-indigo-500/15 font-semibold text-indigo-300'
                    : 'text-white/50 hover:bg-white/5 hover:text-white/70'
                }`
              }
            >
              <Lock className="h-4 w-4" />
              Security
            </NavLink>
          </div>
        </div>
      </div>

      <div className={`border-t px-3 py-3 ${
        isPlatformMode ? 'border-amber-500/20' : 'border-white/10'
      }`}>
        <div className="mb-2 flex items-center gap-2 px-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-xs font-semibold text-white">
            {userInitial}
          </div>
          <div className="min-w-0 flex-1">
            <span className="block truncate text-xs text-white/50">
              {claims?.email ?? 'Loading...'}
            </span>
            {showOrgContext && selectedOrg && (
              <span className="block truncate text-[10px] text-white/30">
                {selectedOrg.orgName}
              </span>
            )}
          </div>
        </div>

        {/* Theme toggle — auto / dark / light */}
        <div className="mb-2 flex items-center gap-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-glass)] p-1">
          {(['auto', 'dark', 'light'] as ThemePreference[]).map((mode) => {
            const icon = mode === 'auto' ? <Monitor className="h-3.5 w-3.5" /> : mode === 'dark' ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />;
            const label = mode === 'auto' ? 'Auto' : mode === 'dark' ? 'Dark' : 'Light';
            const isActive = preference === mode;
            return (
              <button
                key={mode}
                onClick={() => setPreference(mode)}
                title={`Theme: ${label}`}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors ${
                  isActive
                    ? 'bg-indigo-500/15 text-indigo-300'
                    : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-secondary)]'
                }`}
              >
                {icon}
                <span className="hidden sm:inline">{label}</span>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => void logout()}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-white/40 transition-colors hover:bg-white/5 hover:text-white/60"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
        <div className="mt-2 flex items-center gap-1.5 px-3">
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          <span className="text-[10px] text-white/30">System Online</span>
        </div>
      </div>
    </nav>
  );
}
